// common/services/types/listRelationshipTypes.ts
//
// GET /types/relationship — the canonical family/contact relationship vocabulary
// ({id, name}). Cached aggressively by useRelationshipTypes (1h). The endpoint
// returns a top-level array; we also tolerate a { data: [...] } wrapper. The
// "self" entry (the player themselves — the API names it "Me", id 6) is filtered
// out here so every picker hides it without re-implementing the rule.

import req from '../../api/request'
import { SELF_RELATIONSHIP_TYPE_ID } from '../../features/profile/family/constants'

export type RelationshipType = {
  id: number
  name: string
}

// The self entry is named "Me" by the API (and historically "Self"). Match both
// names AND the known id so it stays hidden even if the id or label shifts.
const SELF_RELATIONSHIP_NAMES: ReadonlySet<string> = new Set(['me', 'self'])

const isSelf = (rt: RelationshipType): boolean =>
  rt.id === SELF_RELATIONSHIP_TYPE_ID ||
  SELF_RELATIONSHIP_NAMES.has((rt.name ?? '').trim().toLowerCase())

export const listRelationshipTypes = async (
  // The edit-family flow needs the "Me"/Self entry back so a contact can be
  // marked as the player themselves; every other picker keeps it hidden.
  { includeSelf = false }: { includeSelf?: boolean } = {}
): Promise<RelationshipType[]> => {
  const response = await req('/types/relationship')
  const list = Array.isArray(response)
    ? response
    : Array.isArray((response as { data?: unknown })?.data)
    ? ((response as { data: unknown[] }).data as RelationshipType[])
    : []
  return (list as RelationshipType[]).filter(
    (rt) => rt && (includeSelf || !isSelf(rt))
  )
}
